import { cn } from '@/lib/utils'
import { Tooltip, TooltipTrigger } from '@/components/ui/tooltip'
import ProjectTaskMenu from '@/frontend/features/Fde/ProjectTaskMenu'
import ProjectTaskTooltipContent from '@/frontend/features/Fde/ProjectTaskTooltipContent'
import ProjectTaskMonthNavigation from '@/frontend/features/Fde/ProjectTaskMonthNavigation'
import { TaskPeople, TaskStatusIcon, taskDate } from '@/frontend/features/Fde/projectTaskPresentation'
import type { TaskSurfaceProps } from '@/frontend/features/Fde/ProjectTaskCanvas'
import type { ProjectTask } from '@/frontend/features/Fde/types'

interface Props extends TaskSurfaceProps {
  today: string
  offset: number
  onOffset: (value: number) => void
}
const dayWidth = 32
const barTone = {
  backlog: 'fill-slate-500/10 stroke-slate-500/40',
  todo: 'fill-muted stroke-muted-foreground/40',
  in_progress: 'fill-primary/20 stroke-primary/60',
  blocked: 'fill-amber-500/20 stroke-amber-500/70',
  done: 'fill-emerald-500/20 stroke-emerald-500/60',
}
// UTC calendar arithmetic keeps inclusive ranges stable across daylight-saving changes.
const dayIndex = (value: string, month: Date) =>
  Math.round((Date.parse(value) - month.getTime()) / 86_400_000)

export default function ProjectTaskGantt(props: Props) {
  const { tasks, today, offset, onOffset, copy, onSelect } = props
  const current = new Date(`${today}T00:00:00Z`)
  const month = new Date(Date.UTC(current.getUTCFullYear(), current.getUTCMonth() + offset, 1))
  const days = new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + 1, 0)).getUTCDate()
  const todayIndex = dayIndex(today, month)
  const weekends = Array.from({ length: days }, (_, i) => i).filter((i) =>
    [0, 6].includes(new Date(month.getTime() + i * 86_400_000).getUTCDay()),
  )
  const dated = tasks
    .filter((task) => {
      const start = task.start_on ?? task.due_on
      const end = task.due_on ?? task.start_on
      return start && end && dayIndex(start, month) < days && dayIndex(end, month) >= 0
    })
    .sort(
      (a, b) =>
        (a.start_on ?? a.due_on ?? '').localeCompare(b.start_on ?? b.due_on ?? '') || a.number - b.number,
    )
  const undated = tasks.filter((task) => !task.start_on && !task.due_on)

  function taskRow(task: ProjectTask, timeline: boolean) {
    const start = Math.max(0, dayIndex(task.start_on ?? task.due_on!, month))
    const end = Math.min(days - 1, dayIndex(task.due_on ?? task.start_on!, month))
    const range = Boolean(task.start_on && task.due_on)
    const dates = [
      task.start_on && `${copy.management.task_start_on}: ${taskDate(task.start_on)}`,
      task.due_on && `${copy.management.task_due_on}: ${taskDate(task.due_on)}`,
    ]
      .filter(Boolean)
      .join(' · ')
    return (
      <ProjectTaskMenu key={task.id} {...props} task={task} onSelect={() => onSelect(task.id)}>
        <div className="group flex h-12 w-full items-center border-b border-border/50 text-left hover:bg-muted/30">
          <button
            type="button"
            aria-label={`${task.identifier} ${task.title}`}
            aria-description={`${copy.management.calendar_color_hint} ${copy.management.statuses[task.status]}. ${dates || copy.management.no_due_date}`}
            onClick={() => onSelect(task.id)}
            title={task.title}
            className="sticky left-0 z-10 h-full shrink-0 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
          >
            <div className="sticky left-0 z-10 flex h-full w-56 shrink-0 items-center gap-2 border-r border-border bg-background px-3 group-hover:bg-muted sm:w-80">
              <TaskStatusIcon status={task.status} className="size-3.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <span className="block text-[10px] text-muted-foreground">{task.identifier}</span>
                <span className="line-clamp-2 text-xs leading-4">{task.title}</span>
              </div>
              <TaskPeople
                people={task.assignees}
                emptyLabel={copy.management.task_unassigned}
                compact
                workStateLabels={copy.management.task_work_states}
              />
            </div>
          </button>
          {timeline && (
            <svg
              viewBox={`0 0 ${days * dayWidth} 48`}
              width={days * dayWidth}
              height="48"
              className="shrink-0"
            >
              {weekends.map((i) => (
                <rect key={i} x={i * dayWidth} width={dayWidth} height="48" className="fill-muted/40" />
              ))}
              {todayIndex >= 0 && todayIndex < days && (
                <line
                  x1={(todayIndex + 0.5) * dayWidth}
                  x2={(todayIndex + 0.5) * dayWidth}
                  y1="0"
                  y2="48"
                  className="stroke-primary/30"
                />
              )}
              {range ? (
                <rect
                  x={start * dayWidth + 4}
                  y="14"
                  width={(end - start + 1) * dayWidth - 8}
                  height="20"
                  rx="5"
                  className={cn(barTone[task.status], props.overdueIds.has(task.id) && 'stroke-destructive')}
                />
              ) : (
                <path
                  d={`M ${(start + 0.5) * dayWidth} 17 l 7 7 l -7 7 l -7 -7 Z`}
                  className={cn(barTone[task.status], props.overdueIds.has(task.id) && 'stroke-destructive')}
                />
              )}
              {range && task.due_on && dayIndex(task.due_on, month) < days && (
                <circle cx={(end + 0.5) * dayWidth} cy="24" r="3" className="fill-foreground/50" />
              )}
              <foreignObject
                x={range ? start * dayWidth + 4 : (start + 0.5) * dayWidth - 10}
                y="12"
                width={range ? (end - start + 1) * dayWidth - 8 : 20}
                height="24"
              >
                <Tooltip delayDuration={250}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={`${task.identifier} ${task.title} · ${dates}`}
                      onClick={() => onSelect(task.id)}
                      className="block h-full w-full cursor-pointer rounded-md bg-transparent focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
                    />
                  </TooltipTrigger>
                  <ProjectTaskTooltipContent
                    task={task}
                    copy={copy}
                    overdue={props.overdueIds.has(task.id)}
                    showStart
                  />
                </Tooltip>
              </foreignObject>
            </svg>
          )}
        </div>
      </ProjectTaskMenu>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col p-4 lg:p-5">
      <ProjectTaskMonthNavigation
        month={month}
        offset={offset}
        onOffset={onOffset}
        hint={copy.management.gantt_hint}
        copy={copy}
      />
      <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-border">
        <div className="w-max min-w-full">
          <div className="sticky top-0 z-20 flex h-12 items-center border-b border-border bg-background">
            <span className="sticky left-0 z-30 flex h-full w-56 shrink-0 items-center border-r border-border bg-background px-3 text-xs font-medium sm:w-80">
              {copy.management.tasks}
            </span>
            <svg viewBox={`0 0 ${days * dayWidth} 48`} width={days * dayWidth} height="48" aria-hidden="true">
              {Array.from({ length: days }, (_, i) => (
                <g key={i}>
                  {weekends.includes(i) && (
                    <rect x={i * dayWidth} width={dayWidth} height="48" className="fill-muted/40" />
                  )}
                  {todayIndex === i && (
                    <circle cx={(i + 0.5) * dayWidth} cy="30" r="10" className="fill-primary" />
                  )}
                  <text
                    x={(i + 0.5) * dayWidth}
                    y="13"
                    textAnchor="middle"
                    className="fill-muted-foreground text-[8px]"
                  >
                    {copy.management.weekdays[
                      (new Date(month.getTime() + i * 86_400_000).getUTCDay() + 6) % 7
                    ].slice(0, 1)}
                  </text>
                  <text
                    x={(i + 0.5) * dayWidth}
                    y="33"
                    textAnchor="middle"
                    className={cn(
                      'text-[10px]',
                      todayIndex === i ? 'fill-primary-foreground' : 'fill-muted-foreground',
                    )}
                  >
                    {i + 1}
                  </text>
                </g>
              ))}
            </svg>
          </div>
          {dated.map((task) => taskRow(task, true))}
          {!dated.length && (
            <p className="sticky left-0 w-64 px-3 py-6 text-xs text-muted-foreground">
              {copy.management.no_tasks_in_month}
            </p>
          )}
          {undated.length > 0 && (
            <>
              <p className="sticky left-0 w-64 px-3 pb-2 pt-5 text-xs font-medium text-muted-foreground">
                {copy.management.no_due_date} · {undated.length}
              </p>
              {undated.map((task) => taskRow(task, false))}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
